import * as R from 'ramda'
import { useEffect, useRef, useState } from 'react'
import { unstable_batchedUpdates as batch } from 'react-dom'
import { useSelector } from 'react-redux'
import debounce from 'lodash.debounce'
import { Hall, InviteLink, InviteLinkUsage } from '@weblite/ts-action/hall'
import { GroupMemberChangeablePermissions } from '@weblite/ts-action/subscription'
import { RootState } from '$setup/redux'
import { createUseEntity } from '$func/create-use-entity.react-hook'
import { useAsyncFunction } from '$func/async-function.react-hook'
import {
  getAllInviteLinks,
  getJoinedViaLinkMembers,
  searchInJoinedViaLinkMembers,
} from '$logic/hall/invite-link.service'
import { useSearchedUserByIds } from '$logic/users'
import { getGroupChangeablePermission, getHall } from './hall.service'
import { getRoom } from '$logic/room'
import hostConf from '$config/frontend'
import { HallInviteLink } from './types'
import { hallInviteLinkStorage } from './invite-link.storage'

const getHallsView = (state: RootState): Record<string, Hall> =>
  state.main.halls

const getHallView = (roomId: string) => (state: RootState) =>
  getHallsView(state)[roomId]

export const useHall = createUseEntity<Hall>(getHallView, getHall)

export function useHalls() {
  return useSelector(getHallsView)
}

const memoizedSelector = R.memoizeWith(
  hallId => hallId as string,
  hallId => (state: RootState) =>
    (Object.values(state.main.hallInviteLinks) as HallInviteLink[]).filter(
      inviteLink => inviteLink.roomId === hallId,
    ),
)

const loadingInviteLinksHallIds = new Set<string>()
export function useHallInvitationLinks(hallId: string) {
  const unmountedRef = useRef(false)

  const [loading, setLoading] = useState(true)

  const items = useSelector(memoizedSelector(hallId)) as HallInviteLink[]

  useEffect(() => {
    const currentHallId = hallId
    const unMounted = unmountedRef.current

    if (!loadingInviteLinksHallIds.has(currentHallId)) {
      loadingInviteLinksHallIds.add(currentHallId)
      getAllInviteLinks(hallId)
        .then(res => {
          if (res) {
            hallInviteLinkStorage.add(res)
            loadingInviteLinksHallIds.delete(currentHallId)
          }
          if (unMounted) return
          setLoading(false)
        })
        .catch(() => {
          loadingInviteLinksHallIds.delete(currentHallId)
          if (unMounted) return
          setLoading(false)
        })
    }

    return () => {
      unmountedRef.current = true
    }
  }, [hallId])

  return {
    items,
    loading: loading && items?.length === 0,
  }
}

export function useGroupChangeablePermissionsCount(hallId: string) {
  const { value: decodedPermissions, loading } =
    useAsyncFunction<GroupMemberChangeablePermissions>(
      () => getGroupChangeablePermission(hallId),
      [hallId],
    )

  const permissionsValues = decodedPermissions
    ? R.values(decodedPermissions)
    : []

  const allowedPermissions = R.filter(item => item, permissionsValues)

  return {
    allowedPermissionsCount: allowedPermissions.length,
    permissionsCount: permissionsValues.length,
    loading,
  }
}

export function useJoinedViaLinkMembers(link: InviteLink) {
  const [items, setItems] = useState<InviteLinkUsage[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [hasMoreItemToLoad, setHasMoreItemToLoad] = useState<boolean>(true)
  const [pageToken, setPageToken] = useState<string>()

  const loadMore = async () => {
    const res = await getJoinedViaLinkMembers(link.roomId, link.link, pageToken)
    batch(() => {
      setLoading(false)
      if (!res?.pageToken) setHasMoreItemToLoad(false)
      setPageToken(res?.pageToken)
      setItems([...items, ...(res?.usages || [])])
    })
  }

  return { items, hasMoreItemToLoad, loadMore, loading }
}

const DEBOUNCE_TIME = 700
const debouncedSearchInJoinedViaLinkMembers = debounce(
  searchInJoinedViaLinkMembers,
  DEBOUNCE_TIME,
)

export function useSearchedJoinedViaLinkMembers({
  inviteLink,
  query,
  fetchedItems,
  hasMoreItemToLoad,
}: {
  inviteLink: InviteLink
  query: string
  fetchedItems: InviteLinkUsage[]
  hasMoreItemToLoad: boolean
}) {
  const [items, setItems] = useState<InviteLinkUsage[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    if (hasMoreItemToLoad) {
      setLoading(true)
      debouncedSearchInJoinedViaLinkMembers(
        inviteLink.roomId,
        inviteLink.link,
        query,
      )?.then(res => {
        setItems(res?.usages || [])
        setLoading(false)
      })
    }
  }, [inviteLink, query, hasMoreItemToLoad])

  const { searchedItems: localResult } = useSearchedUserByIds(
    query,
    fetchedItems.map(item => item.userId),
  )

  return {
    loading,
    searchedItems: loading
      ? localResult.map(id => fetchedItems.find(item => item.userId === id))
      : items,
  }
}

export const useHallInfo = (isHall: boolean, roomId: string) => {
  if (!isHall) return {}

  return {
    hall: useHall(roomId).entity,
  }
}

const MOOKEB_TAG = 'mookeb'
const LIVE_TAG = 'live_integrated'

const isRoomMookeb = async (roomId: string): Promise<boolean | undefined> => {
  const room = await getRoom(roomId)
  if (room?.type !== 'CHANNEL') return false

  const hall = await getHall(roomId)
  return hall?.tags?.includes(MOOKEB_TAG)
}

export function useRoomIsMookeb(roomId: string) {
  const { value, loading } = useAsyncFunction(
    async () => isRoomMookeb(roomId),
    [roomId],
  )

  return { roomIsMookeb: value, loading }
}

const canRoomMakeLive = async (
  roomId: string,
): Promise<boolean | undefined> => {
  if (!hostConf.VITE_PROVIDE_CALL_BASED_ON_TAGS) return true
  const room = await getRoom(roomId)
  if (room?.type !== 'CHANNEL') return false

  const hall = await getHall(roomId)
  return hall?.tags?.includes(LIVE_TAG)
}

export function useRoomCanMakeLive(roomId: string) {
  const { value, loading } = useAsyncFunction(
    async () => canRoomMakeLive(roomId),
    [roomId],
  )

  return { canMakeLive: value, loading }
}
